import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Stack,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { BootstrapDialog } from 'components/BootstrapDialog'
import { DialogCloseButton } from 'components/DialogCloseButton'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useUserRequestOrganizationMutation } from 'services/organizations'
import { OrganizationType } from 'types/organization'
import { createOrganizationSchema } from './validations'

export type OrganizationFieldValues = {
  name: string
  executiveOfficerName: string
  type: OrganizationType
  userRequest: {
    firstName: string
    lastName: string
    pin: string
    password: string
  }
}

type Props = {
  onClose: () => void
}

export const CreateUserRequestOrganization = ({ onClose }: Props) => {
  const { t } = useTranslation()
  const schema = createOrganizationSchema(t)

  const [createOrganization, { isSuccess, isError }] =
    useUserRequestOrganizationMutation()

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<OrganizationFieldValues>({
    defaultValues: {
      name: '',
      executiveOfficerName: '',
      type: OrganizationType.CLIENT,
      userRequest: {
        firstName: '',
        lastName: '',
        pin: '',
        password: '',
      },
    },
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    if (isSuccess) {
      onClose()
      toast.success(t('operation-success'))
    }
    if (isError) {
      toast.error(t('failedToCreate'))
    }
  }, [isSuccess, isError, t, onClose])

  const onSubmit = (values: OrganizationFieldValues) => {
    createOrganization(values)
  }

  return (
    <BootstrapDialog open maxWidth="sm" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, fontWeight: 'bold' }}>
        {t('organizations')}
      </DialogTitle>

      <DialogCloseButton handleClose={onClose} />

      <DialogContent dividers>
        <Stack spacing={2}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Box>
                <TextField
                  {...field}
                  fullWidth
                  required
                  label={t('name')}
                  size="small"
                  variant="outlined"
                />
                {errors.name && (
                  <FormHelperText error>{errors.name?.message}</FormHelperText>
                )}
              </Box>
            )}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel required id="type">
              {t('organization-type')}
            </InputLabel>
            <Controller
              name="type"
              control={control}
              rules={{ required: t('required') }}
              render={({ field }) => (
                <Select
                  {...field}
                  labelId="type"
                  required
                  size="small"
                  label={t('organization-type')}
                >
                  {[OrganizationType.CLIENT, OrganizationType.TENDER_AUTHORITY].map(
                    (type) => (
                      <MenuItem value={type} key={type}>
                        {t(`${type}`)}
                      </MenuItem>
                    )
                  )}
                </Select>
              )}
            />
          </FormControl>
          <Controller
            name="executiveOfficerName"
            control={control}
            render={({ field }) => (
              <Box>
                <TextField
                  {...field}
                  fullWidth
                  required
                  label={t('executive-officer-name')}
                  size="small"
                  variant="outlined"
                />
                {errors.executiveOfficerName && (
                  <FormHelperText error>
                    {errors.executiveOfficerName?.message}
                  </FormHelperText>
                )}
              </Box>
            )}
          />
          <Controller
            name="userRequest.firstName"
            control={control}
            render={({ field }) => (
              <Box>
                <TextField
                  {...field}
                  fullWidth
                  required
                  label={t('user-firstName')}
                  size="small"
                  variant="outlined"
                />
                {errors?.userRequest?.firstName && (
                  <FormHelperText error>
                    {errors?.userRequest?.firstName?.message}
                  </FormHelperText>
                )}
              </Box>
            )}
          />
          <Controller
            name="userRequest.lastName"
            control={control}
            render={({ field }) => (
              <Box>
                <TextField
                  {...field}
                  fullWidth
                  required
                  label={t('user-lastName')}
                  size="small"
                  variant="outlined"
                />
                {errors?.userRequest?.lastName && (
                  <FormHelperText error>
                    {errors?.userRequest?.lastName?.message}
                  </FormHelperText>
                )}
              </Box>
            )}
          />
          <Controller
            name="userRequest.pin"
            control={control}
            render={({ field }) => (
              <Box>
                <TextField
                  {...field}
                  fullWidth
                  required
                  label={t('user-pin')}
                  size="small"
                  variant="outlined"
                />
                {errors?.userRequest?.pin && (
                  <FormHelperText error>
                    {errors?.userRequest?.pin?.message}
                  </FormHelperText>
                )}
              </Box>
            )}
          />
          <Controller
            name="userRequest.password"
            control={control}
            render={({ field }) => (
              <Box>
                <TextField
                  {...field}
                  fullWidth
                  label={t('user-password')}
                  size="small"
                  variant="outlined"
                />
                {errors?.userRequest?.password && (
                  <FormHelperText error>
                    {errors?.userRequest?.password?.message}
                  </FormHelperText>
                )}
              </Box>
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack width="100%" direction="row" justifyContent="space-between">
          <Button onClick={onClose} variant="outlined">
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
            autoFocus
            onClick={handleSubmit(onSubmit)}
          >
            {t('create')}
          </Button>
        </Stack>
      </DialogActions>
    </BootstrapDialog>
  )
}
